import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchWorkerDetail, fetchProject } from "../../api/memberApi";
import { useNotification } from "../../providers/NotificationProvider";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function WorkerProjectGoalAchievementListPage() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const worker = await fetchWorkerDetail(workerId);
      const projectId = worker?.projectId;
      if (!projectId) {
        setLoading(false);
        return;
      }

      const project = await fetchProject(projectId);
      setProjectInfo({
        name: project.projectName || "",
        description: project.projectDescription || "",
        startDate: project.startDate || "",
        endDate: project.endDate || "",
      });

      const goals = project.goals || [];
      const achievements = project.achievements || [];

      const combined = goals.map((goal, idx) => {
        const indicatorName = goal.indicator?.indicatorName || "";
        const matched = achievements
          .filter((a) => a.goal?.indicator?.indicatorName === indicatorName)
          .sort((a, b) => {
            if (!a.actualDate && !b.actualDate) return 0;
            if (!a.actualDate) return 1;
            if (!b.actualDate) return -1;
            return new Date(b.actualDate) - new Date(a.actualDate);
          });
        const latest = matched[0] || null;
        return {
          id: idx,
          indicatorName,
          unit: goal.indicator?.unit || "",
          plannedValue: goal.plannedValue ?? "",
          plannedDate: goal.plannedDate || "",
          deadline: goal.deadline || "",
          actualValue: latest?.actualValue ?? "",
          actualDate: latest?.actualDate || "",
          achievementCount: matched.length,
        };
      });

      setRows(combined);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [workerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}`)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("worker.project.goalAchievement.title")}
        </Typography>
      </Box>

      {projectInfo && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {projectInfo.name}
          </Typography>
          {projectInfo.description && (
            <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 56 }}>{t("common.description")}:</Typography>
              <Typography variant="body2">{projectInfo.description}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 3 }}>
            {projectInfo.startDate && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">{t("workflow.startDate")}:</Typography>
                <Typography variant="body2">{formatDate(projectInfo.startDate)}</Typography>
              </Box>
            )}
            {projectInfo.endDate && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">{t("workflow.endDate")}:</Typography>
                <Typography variant="body2">{formatDate(projectInfo.endDate)}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t("goalAchievement.noData")}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t("goalAchievement.indicator")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t("goalAchievement.plannedValue")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t("goalAchievement.actualValue")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("goalAchievement.unit")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("goalAchievement.plannedDate")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("goalAchievement.deadline")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("goalAchievement.actualDate")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t("goalAchievement.count")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.indicatorName || "—"}</TableCell>
                  <TableCell align="right">{row.plannedValue !== "" ? row.plannedValue : "—"}</TableCell>
                  <TableCell align="right">{row.actualValue !== "" ? row.actualValue : "—"}</TableCell>
                  <TableCell>{row.unit || "—"}</TableCell>
                  <TableCell>{formatDate(row.plannedDate)}</TableCell>
                  <TableCell>{formatDate(row.deadline)}</TableCell>
                  <TableCell>{formatDate(row.actualDate)}</TableCell>
                  <TableCell align="right">{row.achievementCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
